<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import QaDashboardApi from 'dashboard/api/qaDashboard';
import '../styles/qaShell.css';

const QA_APPEALS_V1 = 'QA_APPEALS_V1';

const { t } = useI18n();
const store = useStore();

const isLoading = ref(false);
const errorText = ref('');
const appeals = ref([]);
const statusFilter = ref('');
const busyId = ref(null);
const notes = reactive({});
const compileMarker = computed(() => QA_APPEALS_V1);

const agents = computed(() => store.getters['agents/getAgents'] || []);
const agentName = id => {
  if (!id) return '—';
  const agent = agents.value.find(a => Number(a.id) === Number(id));
  return agent
    ? agent.name || agent.available_name || agent.email || `#${id}`
    : `#${id}`;
};

const roleLabel = role =>
  role === 'agent'
    ? t('QA_DASHBOARD.APPEALS.OPENED_BY_AGENT')
    : t('QA_DASHBOARD.APPEALS.OPENED_BY_REVIEWER');

const statusTone = status =>
  ({
    open: 'qa-badge--warn',
    approved: 'qa-badge--good',
    denied: 'qa-badge--bad',
  })[status] || 'qa-badge--muted';

const load = async () => {
  isLoading.value = true;
  errorText.value = '';
  try {
    const { data } = await QaDashboardApi.getAppeals(
      statusFilter.value ? { status: statusFilter.value } : {}
    );
    appeals.value = data.appeals || [];
  } catch (error) {
    errorText.value = t('QA_DASHBOARD.APPEALS.ERROR_LOAD');
    useAlert(errorText.value);
  } finally {
    isLoading.value = false;
  }
};

const decide = async (appeal, decision) => {
  const confirmMsg =
    decision === 'approve'
      ? t('QA_DASHBOARD.APPEALS.CONFIRM_APPROVE')
      : t('QA_DASHBOARD.APPEALS.CONFIRM_DENY');
  // eslint-disable-next-line no-alert
  if (!window.confirm(confirmMsg)) return;
  busyId.value = appeal.id;
  try {
    const payload = { note: notes[appeal.id] || '' };
    if (decision === 'approve') {
      await QaDashboardApi.approveAppeal(appeal.id, payload);
      useAlert(t('QA_DASHBOARD.APPEALS.APPROVED_TOAST'));
    } else {
      await QaDashboardApi.denyAppeal(appeal.id, payload);
      useAlert(t('QA_DASHBOARD.APPEALS.DENIED_TOAST'));
    }
    delete notes[appeal.id];
    await load();
  } catch (error) {
    useAlert(
      error?.response?.data?.error || t('QA_DASHBOARD.APPEALS.ERROR_ACTION')
    );
  } finally {
    busyId.value = null;
  }
};

onMounted(() => {
  store.dispatch('agents/get');
  load();
});
</script>

<template>
  <div class="qa-page" :data-qa-compile-marker="compileMarker">
    <div class="qa-shell">
      <div class="qa-header">
        <div class="qa-header-left">
          <h1 class="qa-title">{{ t('QA_DASHBOARD.APPEALS.TITLE') }}</h1>
          <p class="qa-desc">{{ t('QA_DASHBOARD.APPEALS.DESCRIPTION') }}</p>
        </div>
        <div class="qa-header-actions">
          <button
            type="button"
            class="qa-btn"
            :disabled="isLoading"
            @click="load"
          >
            {{ t('QA_DASHBOARD.APPEALS.REFRESH') }}
          </button>
        </div>
      </div>

      <div class="qa-pill qa-pill--muted qa-appeals-note">
        {{ t('QA_DASHBOARD.APPEALS.OBSERVE_NOTE') }}
      </div>

      <div class="qa-toolbar">
        <div class="qa-toolbar-left">
          <label class="qa-field">
            <span class="qa-label">{{
              t('QA_DASHBOARD.APPEALS.FILTER_STATUS')
            }}</span>
            <select
              v-model="statusFilter"
              class="qa-select"
              :disabled="isLoading"
              @change="load"
            >
              <option value="">
                {{ t('QA_DASHBOARD.APPEALS.STATUS_ALL') }}
              </option>
              <option value="open">
                {{ t('QA_DASHBOARD.APPEALS.STATUS_OPEN') }}
              </option>
              <option value="approved">
                {{ t('QA_DASHBOARD.APPEALS.STATUS_APPROVED') }}
              </option>
              <option value="denied">
                {{ t('QA_DASHBOARD.APPEALS.STATUS_DENIED') }}
              </option>
              <option value="withdrawn">
                {{ t('QA_DASHBOARD.APPEALS.STATUS_WITHDRAWN') }}
              </option>
            </select>
          </label>
        </div>
      </div>

      <div class="qa-content">
        <p v-if="errorText" class="qa-error">{{ errorText }}</p>

        <div v-if="!isLoading && !appeals.length" class="qa-card">
          <div class="qa-card-body">
            <h3 class="qa-card-title">
              {{ t('QA_DASHBOARD.APPEALS.EMPTY_TITLE') }}
            </h3>
            <p class="qa-desc">{{ t('QA_DASHBOARD.APPEALS.EMPTY_BODY') }}</p>
          </div>
        </div>

        <div v-else class="qa-card">
          <div class="qa-card-body qa-card-body--flush">
            <div class="qa-table-wrap">
              <table class="qa-table">
                <thead>
                  <tr>
                    <th class="qa-th">{{ t('QA_DASHBOARD.APPEALS.TH_ID') }}</th>
                    <th class="qa-th qa-th--center">
                      {{ t('QA_DASHBOARD.APPEALS.TH_INCIDENT') }}
                    </th>
                    <th class="qa-th">
                      {{ t('QA_DASHBOARD.APPEALS.TH_OPENED_BY') }}
                    </th>
                    <th class="qa-th">
                      {{ t('QA_DASHBOARD.APPEALS.TH_REASON') }}
                    </th>
                    <th class="qa-th qa-th--center">
                      {{ t('QA_DASHBOARD.APPEALS.TH_STATUS') }}
                    </th>
                    <th class="qa-th qa-th--center">
                      {{ t('QA_DASHBOARD.APPEALS.TH_VERDICT') }}
                    </th>
                    <th class="qa-th">
                      {{ t('QA_DASHBOARD.APPEALS.TH_ACTIONS') }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="appeal in appeals" :key="appeal.id">
                    <td class="qa-td">{{ appeal.id }}</td>
                    <!-- prettier-ignore -->
                    <td class="qa-td qa-th--center">#{{ appeal.qa_incident_id }}</td>
                    <td class="qa-td">
                      {{ roleLabel(appeal.opened_by_role) }}
                      <span class="qa-subtle">
                        · {{ agentName(appeal.opened_by_id) }}</span
                      >
                    </td>
                    <td class="qa-td qa-td--pre">{{ appeal.reason }}</td>
                    <td class="qa-td qa-th--center">
                      <span
                        class="qa-badge"
                        :class="statusTone(appeal.status)"
                        >{{ appeal.status }}</span
                      >
                    </td>
                    <td class="qa-td qa-th--center">
                      <span v-if="appeal.verdict_to" class="qa-subtle">
                        {{ appeal.verdict_from }} → {{ appeal.verdict_to }}
                      </span>
                      <span v-else class="qa-subtle">{{
                        appeal.verdict_from
                      }}</span>
                    </td>
                    <td class="qa-td">
                      <template v-if="appeal.status === 'open'">
                        <div class="qa-actions qa-actions--compact">
                          <input
                            v-model="notes[appeal.id]"
                            type="text"
                            class="qa-input"
                            :placeholder="
                              t('QA_DASHBOARD.APPEALS.NOTE_PLACEHOLDER')
                            "
                          />
                          <button
                            type="button"
                            class="qa-btn qa-btn--sm qa-btn--primary"
                            :disabled="busyId === appeal.id"
                            @click="decide(appeal, 'approve')"
                          >
                            {{ t('QA_DASHBOARD.APPEALS.APPROVE') }}
                          </button>
                          <button
                            type="button"
                            class="qa-btn qa-btn--sm qa-btn--danger"
                            :disabled="busyId === appeal.id"
                            @click="decide(appeal, 'deny')"
                          >
                            {{ t('QA_DASHBOARD.APPEALS.DENY') }}
                          </button>
                        </div>
                      </template>
                      <span v-else class="qa-subtle">{{
                        appeal.resolution_note || '—'
                      }}</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* The note sits in the slot between the sticky header and the toolbar, both of
   which are inset 24px by qaShell.css. It is a direct child of .qa-shell with
   no inset of its own, so it started flush against the card border while its
   neighbours above and below did not. The rest of the page's content is inside
   .qa-content, which supplies the same inset the other QA pages use.

   Vertically it had nothing above it and sat right on the header's bottom
   rule. 16px is what .qa-content puts between that rule and a page's first
   block everywhere else, so the note now clears the divider by exactly as much
   as ordinary content does. Below it the toolbar contributes its own 12px of
   padding before the Status field, so 4px here brings that side to a matching
   16px rather than the 24px the old 12px produced. */
.qa-appeals-note {
  margin: 16px 24px 4px;
}

/* Matches the breakpoint qaShell.css uses to pull .qa-header and .qa-toolbar
   in to 16px, so the note stays aligned with them on narrow screens. */
@media (max-width: 1100px) {
  .qa-appeals-note {
    margin-left: 16px;
    margin-right: 16px;
  }
}
</style>
